package com.vizio.redwolf.dms.homesetup

import com.vizio.redwolf.dms.model.FieldedStatusRequestInfo
import com.vizio.redwolf.dms.model.LinkNonceRequestBody
import com.vizio.redwolf.dms.model.LinkNonceRequestInfo
import com.vizio.redwolf.dms.model.RegisterDeviceRequestRequestBody
import com.vizio.redwolf.dms.model.RegistrationStatusRequestInfo
import com.vizio.redwolf.dms.model.RequestNonceRequestInfo
import com.vizio.redwolf.dms.model.UnregisterDeviceRequestBody
import com.vizio.redwolf.utils.AppConfig
import com.vizio.redwolf.utils.CONTENT_TYPE_APP_JSON_UTF8
import com.vizio.redwolf.utils.ClientConfig
import com.vizio.redwolf.utils.HttpResult
import com.vizio.redwolf.utils.applyAppConfig
import com.vizio.redwolf.utils.applyTimeout
import com.vizio.redwolf.utils.safeHttpCall
import com.vizio.redwolf.utils.setSentryHeader
import com.vizio.redwolf.utils.setTraceId
import com.vizio.redwolf.utils.setTvAppId
import io.ktor.client.HttpClient
import io.ktor.client.request.forms.formData
import io.ktor.client.request.get
import io.ktor.client.request.headers
import io.ktor.client.request.parameter
import io.ktor.client.request.post
import io.ktor.client.request.setBody
import io.ktor.client.statement.HttpResponse
import io.ktor.http.HttpHeaders
import io.ktor.http.URLProtocol
import io.ktor.http.path
import kotlinx.serialization.json.JsonElement

class HomeSetupHttpClient(private val env: HomeSetupEnvironment, private val appConfig: AppConfig, private val clientConfig: ClientConfig) : HomeSetupClient {

    private val httpClient: HttpClient = clientConfig.clientProvider.httpClient

    override suspend fun requestNonceStatusFielded(requestInfo: FieldedStatusRequestInfo, sentryHeader: String?, tvAppId: String?): HttpResult<HttpResponse> {
        return safeHttpCall {
            httpClient.get {
                url {
                    protocol = URLProtocol.HTTPS
                    host = env.host
                    path("${env.envPath}/api/devices/request/pay/status/fielded/${requestInfo.pin}")
                    if(requestInfo.isLookupUserId != null){
                        parameter(LOOKUP_USERID_KEY, requestInfo.isLookupUserId)
                    }
                }
                headers {
                    append(HEADER_KEY_X_API_KEY, env.v2ApiKey)
                    append(HttpHeaders.ContentType, CONTENT_TYPE_APP_JSON_UTF8)
                    setSentryHeader(sentryHeader)
                    setTvAppId(tvAppId)
                }
                applyAppConfig(appConfig)
                applyTimeout(clientConfig.timeout)
            }
        }
    }

    override suspend fun updateVoiceLinking(voiceRequestBody: JsonElement, deviceInstanceId: String, sentryHeader: String?, tvAppId: String?, traceId: String?): HttpResult<HttpResponse> {
        return safeHttpCall {
            httpClient.post {
                url {
                    protocol = URLProtocol.HTTPS
                    host = env.host
                    path("${env.envPath}/api/devices/instance/${deviceInstanceId}/voice")
                }
                setBody(voiceRequestBody)
                headers {
                    append(HttpHeaders.Authorization, env.jwtVoiceToken)
                    append(HttpHeaders.ContentType, CONTENT_TYPE_APP_JSON_UTF8)
                    setSentryHeader(sentryHeader)
                    setTvAppId(tvAppId)
                    setTraceId(traceId)
                }
                applyAppConfig(appConfig)
                applyTimeout(clientConfig.timeout)
            }
        }
    }

    override suspend fun requestNonce(requestNonceRequestInfo: RequestNonceRequestInfo, sentryHeader: String?, tvAppId: String?, traceId: String?): HttpResult<HttpResponse> {
        val registerDeviceRequestRequestBody = RegisterDeviceRequestRequestBody(
            requestNonceRequestInfo.deviceInstanceId,
            requestNonceRequestInfo.deviceInstanceId,
            requestNonceRequestInfo.friendlyName,
            requestNonceRequestInfo.modelName,
            requestNonceRequestInfo.serialNumber,
            false,
            skipCreditCard = requestNonceRequestInfo.skipCreditCard,
            launchSource = requestNonceRequestInfo.launchSource
        )
        return safeHttpCall {
            httpClient.post {
                url {
                    protocol = URLProtocol.HTTPS
                    host = env.host
                    path("${env.envPath}/api/devices/request/pay")
                }
                setBody(registerDeviceRequestRequestBody)
                headers {
                    append(HttpHeaders.Authorization, env.jwtToken)
                    append(HttpHeaders.ContentType, CONTENT_TYPE_APP_JSON_UTF8)
                    setSentryHeader(sentryHeader)
                    setTvAppId(tvAppId)
                    setTraceId(traceId)
                }
                applyAppConfig(appConfig)
                applyTimeout(clientConfig.timeout)
            }
        }
    }

    override suspend fun linkNonce(linkNonceRequestInfo: LinkNonceRequestInfo, sentryHeader: String?, tvAppId: String?, traceId: String?): HttpResult<HttpResponse> {
        val linkNonceRequestBody = LinkNonceRequestBody(
            linkNonceRequestInfo.authorizationCode,
            linkNonceRequestInfo.pendingDevicePin,
            linkNonceRequestInfo.state
        )
        return safeHttpCall {
            httpClient.post {
                url {
                    protocol = URLProtocol.HTTPS
                    host = env.host
                    path("${env.envPath}/api/devices/register/pay")
                }
                setBody(linkNonceRequestBody)
                headers {
                    append(HttpHeaders.Authorization, env.jwtToken)
                    append(HttpHeaders.ContentType, CONTENT_TYPE_APP_JSON_UTF8)
                    setSentryHeader(sentryHeader)
                    setTvAppId(tvAppId)
                    setTraceId(traceId)
                }
                applyAppConfig(appConfig)
                applyTimeout(clientConfig.timeout)
            }
        }
    }

    override suspend fun unlink(deviceId: String, sentryHeader: String?, tvAppId: String?, traceId: String?): HttpResult<HttpResponse> {
        return safeHttpCall {
            httpClient.post {
                url {
                    protocol = URLProtocol.HTTPS
                    host = env.host
                    path("${env.envPath}/api/devices/unregisterall")
                }
                setBody(UnregisterDeviceRequestBody(deviceId))
                headers {
                    append(HttpHeaders.Authorization, env.jwtToken)
                    append(HttpHeaders.ContentType, CONTENT_TYPE_APP_JSON_UTF8)
                    setSentryHeader(sentryHeader)
                    setTvAppId(tvAppId)
                    setTraceId(traceId)
                }
                formData {
                    parameter(PARAM_SERVICE, SERVICE_HOME)
                }
                applyAppConfig(appConfig)
                applyTimeout(clientConfig.timeout)
            }
        }
    }

    override suspend fun getLinkedDevices(myVizioBearerToken: String, sentryHeader: String?, tvAppId: String?, traceId: String?): HttpResult<HttpResponse> {
        return safeHttpCall {
            httpClient.get {
                url {
                    protocol = URLProtocol.HTTPS
                    host = env.host
                    path("${env.envPath}/v2/api/devices/user")
                }
                headers {
                    append(HttpHeaders.Authorization, env.jwtToken)
                    append(HttpHeaders.ContentType, CONTENT_TYPE_APP_JSON_UTF8)
                    append(HEADER_KEY_X_VIZIO_TOKEN, myVizioBearerToken)
                    setSentryHeader(sentryHeader)
                    setTvAppId(tvAppId)
                    setTraceId(traceId)
                }
                applyAppConfig(appConfig)
                applyTimeout(clientConfig.timeout)
            }
        }
    }

    override suspend fun requestRegistrationStatus(requestInfo: RegistrationStatusRequestInfo, traceId: String?): HttpResult<HttpResponse> {
        return safeHttpCall {
            httpClient.get {
                url {
                    protocol = URLProtocol.HTTPS
                    host = env.host
                    path("${env.envPath}/api/devices/instance/${requestInfo.deviceInstanceId}/status")
                }
                headers {
                    append(HEADER_KEY_X_API_KEY, env.apiKey)
                    append(HttpHeaders.ContentType, CONTENT_TYPE_APP_JSON_UTF8)
                    setTraceId(traceId)
                }
                applyAppConfig(appConfig)
                applyTimeout(clientConfig.timeout)
            }
        }
    }

    companion object {
        private const val HEADER_KEY_X_API_KEY = "x-api-key"
        private const val PARAM_SERVICE = "service"
        private const val HEADER_KEY_X_VIZIO_TOKEN = "x-vizio-token"
        private const val LOOKUP_USERID_KEY = "lookupUserId"

        // Vizio Home Service
        private const val SERVICE_HOME = "home"
    }
}
